import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { AlertTriangle, ChevronRight, FileSignature, UserRound } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { CornerDots } from "../../../../../components/dashboard/shared";
import { StatefulButton } from "@/components/motion/button";
import { ButtonRouterLink } from "@/components/motion/button/router-link";
import { Input } from "@/components/motion/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/motion/select";
import { Textarea } from "@/components/motion/textarea";
import { domainAliasesQueryKey } from "@/features/identities/queries";
import { getVerifiedDomains, submitNewAlias } from "@/features/identities/server-fns";
import { mapAliasToView, type IdentityAliasView } from "@/features/identities/view-model";
import { capturePostHogEvent } from "@/lib/posthog-client";

const LOCAL_PART_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+$/;

export const Route = createFileRoute("/dashboard/identities/aliases/new/")({
  head: () => ({
    meta: [{ title: "New Alias - Bavimail" }],
  }),
  validateSearch: (search: Record<string, unknown>) => ({
    domain: (search.domain as string) || "",
  }),
  loader: async () => await getVerifiedDomains(),
  component: NewAliasPage,
});

function NewAliasPage() {
  const { domain: preselectedDomainId } = Route.useSearch();
  const verifiedDomains = Route.useLoaderData();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);

  const [localPart, setLocalPart] = useState("");
  const [selectedDomainId, setSelectedDomainId] = useState(
    preselectedDomainId || verifiedDomains[0]?.id || "",
  );
  const [signatureHtml, setSignatureHtml] = useState("");
  const [creating, setCreating] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const selectedDomain = verifiedDomains.find((domain) => domain.id === selectedDomainId);
  const normalizedLocalPart = localPart.trim().toLowerCase().replace(/\s+/g, "");
  const fullEmail = selectedDomain ? `${normalizedLocalPart}@${selectedDomain.domain}` : "";
  const canSubmit = Boolean(
    selectedDomainId && normalizedLocalPart && LOCAL_PART_REGEX.test(normalizedLocalPart),
  );

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!canSubmit) {
      setErrorMessage("Enter a valid alias local part and choose a verified domain.");
      return;
    }

    try {
      setErrorMessage(null);
      setCreating(true);
      const created = await submitNewAlias({
        data: {
          domainId: selectedDomainId,
          alias: normalizedLocalPart,
          signatureHtml: signatureHtml.trim() || null,
        },
      });
      const aliasView = mapAliasToView(created);
      queryClient.setQueryData<IdentityAliasView[]>(
        domainAliasesQueryKey(selectedDomainId),
        (current) => {
          if (!current) {
            return [aliasView];
          }

          const existingIndex = current.findIndex((alias) => alias.id === aliasView.id);
          if (existingIndex >= 0) {
            return current.map((alias) => (alias.id === aliasView.id ? aliasView : alias));
          }

          return [aliasView, ...current];
        },
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["verifiedIdentityOptions"] }),
        queryClient.invalidateQueries({ queryKey: ["composeOptions"] }),
      ]);
      capturePostHogEvent("alias_created", { email: fullEmail });

      await navigate({
        to: "/dashboard/identities",
        search: { domainId: selectedDomainId, setup: undefined },
      });
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "Failed to create the alias.");
      setCreating(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex h-full flex-col">
      <div className="flex shrink-0 items-center border-b border-dashed border-hb-border px-6 py-4">
        <div className="flex items-center gap-4">
          <Link
            to="/dashboard/identities"
            search={{ domainId: selectedDomainId || undefined, setup: undefined }}
            className="flex items-center gap-2 font-mono-hb text-xs text-hb-muted transition-colors hover:text-hb-accent"
          >
            <ChevronRight className="h-3.5 w-3.5 rotate-180" />
            Identities
          </Link>
          <div className="h-4 w-px bg-hb-card" />
          <div className="flex items-center gap-3">
            <UserRound className="h-4 w-4 text-hb-accent" />
            <h1 className="font-hb text-lg font-extrabold tracking-tight text-hb-heading">
              New Alias
            </h1>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-[1100px] px-6 py-8 lg:px-8">
          <div className="grid grid-cols-1 items-start gap-6 md:grid-cols-[1fr_340px]">
            <div className="relative border border-dashed border-hb-card bg-hb-surface">
              <CornerDots />
              <div className="space-y-6 p-6">
                {verifiedDomains.length === 0 ? (
                  <div className="rounded-sm border border-dashed border-hb-accent/20 bg-hb-accent/5 px-4 py-3">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-hb-accent" />
                      <p className="font-mono-hb text-[11px] leading-relaxed text-hb-accent/80">
                        You need at least one verified domain before you can create an alias.
                      </p>
                    </div>
                  </div>
                ) : null}

                <Select
                  value={selectedDomainId}
                  onValueChange={setSelectedDomainId}
                  required
                  disabled={verifiedDomains.length === 0 || creating}
                  label={
                    <>
                      Domain <span className="text-hb-accent">*</span>
                    </>
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="No verified domains" />
                  </SelectTrigger>
                  <SelectContent>
                    {verifiedDomains.map((domain) => (
                      <SelectItem key={domain.id} value={domain.id}>
                        {domain.domain}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <div>
                  <Input
                    ref={inputRef}
                    type="text"
                    label={
                      <>
                        Alias local part <span className="text-hb-accent">*</span>
                      </>
                    }
                    value={localPart}
                    onChange={(value) => setLocalPart(value.toLowerCase().replace(/\s+/g, ""))}
                    placeholder="support"
                    disabled={creating}
                    autoComplete="off"
                    autoCapitalize="none"
                    spellCheck={false}
                  />
                  <p className="mt-2 font-mono-hb text-[10px] text-hb-muted">
                    Only the local part is editable here. Bavimail stores the full email
                    automatically.
                  </p>
                </div>

                <div>
                  <Textarea
                    label={
                      <>
                        <FileSignature className="h-3.5 w-3.5" />
                        HTML signature
                      </>
                    }
                    classNames={{ label: "flex items-center gap-2" }}
                    value={signatureHtml}
                    onChange={setSignatureHtml}
                    rows={6}
                    disabled={creating}
                    placeholder="<p><strong>Support Team</strong><br/>Bavimail</p>"
                  />
                </div>

                {errorMessage ? (
                  <div className="font-mono-hb text-[11px] text-hb-danger">{errorMessage}</div>
                ) : null}
              </div>

              <div className="flex items-center gap-3 border-t border-dashed border-hb-border p-6">
                <StatefulButton
                  size="lg"
                  type="submit"
                  shape="sharp"
                  state={creating ? "loading" : "idle"}
                  loadingText="Creating"
                  disabled={!canSubmit || verifiedDomains.length === 0}
                >
                  Create Alias
                </StatefulButton>
                <ButtonRouterLink
                  size="lg"
                  to="/dashboard/identities"
                  search={{ domainId: selectedDomainId || undefined, setup: undefined }}
                  variant="ghost"
                  shape="sharp"
                >
                  Cancel
                </ButtonRouterLink>
              </div>
            </div>

            <div className="relative border border-dashed border-hb-card bg-hb-surface">
              <CornerDots />
              <div className="border-b border-dashed border-hb-border px-6 py-4">
                <h2 className="font-hb text-sm font-bold text-hb-heading">Preview</h2>
              </div>
              <div className="space-y-4 p-6">
                <div>
                  <div className="font-mono-hb text-[10px] uppercase tracking-[0.15em] text-hb-faint">
                    Full email
                  </div>
                  <div className="mt-2 rounded-sm border border-dashed border-hb-border bg-hb-inset px-4 py-3 font-mono-hb text-sm text-hb-heading">
                    {fullEmail || "support@example.com"}
                  </div>
                </div>

                <div>
                  <div className="font-mono-hb text-[10px] uppercase tracking-[0.15em] text-hb-faint">
                    What gets saved
                  </div>
                  <ul className="mt-2 space-y-2 font-mono-hb text-[11px] text-hb-subtle">
                    <li>- alias local part</li>
                    <li>- linked verified domain</li>
                    <li>- HTML signature</li>
                  </ul>
                </div>

                <div className="rounded-sm border border-dashed border-hb-border bg-hb-inset px-4 py-3 font-mono-hb text-[11px] leading-relaxed text-hb-muted">
                  Reply-to, default alias selection, and extra alias profile fields are
                  intentionally deferred until the later backend phases.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
}
